import {Component, computed, DestroyRef, EventEmitter, OnInit, Output, Signal} from '@angular/core';
import {Router} from '@angular/router';
import {Actions, ofType} from '@ngrx/effects';
import {select, Store} from '@ngrx/store';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  switchMap,
  tap
} from 'rxjs';
import {AuthService} from '../../core/service/auth.service';
import {FeatureToggleService} from '../../core/service/feature-toggle.service';
import {SupervisorService} from '../../core/service/supervisor.service';
import {UserService} from '../../core/service/user.service';
import {AvailableToggle} from '../../shared/model/feature-toggle.model';
import {IOpportunity, OpportunityStatus} from '../../shared/model/opportunity.model';
import {OrganizationStatus} from '../../shared/model/organization.model';
import {ISidebarOrganization} from '../../shared/model/sidebar-organization.model';
import {ISupervisor} from '../../shared/model/supervisor.model';
import {IServiceRegistration} from "../../shared/model/service-registration.model";
import {updateOrganizationSuccessAction} from '../../core/store/organization/organization.actions';
import {loadPastTermRegistrations} from "../../core/store/past-term-registrations/past-term-registration.actions";
import {
  selectNewPastTermRegistrations
} from "../../core/store/past-term-registrations/past-term-registrations.selectors";
import {takeUntilDestroyed, toSignal} from "@angular/core/rxjs-interop";
import {MessageService} from "../../core/service/message.service";
import {
  findServiceRegistrationsForOpportunitiesByStatus
} from "../../core/store/service-registrations/service-registrations.actions";
import {
  selectActionItemsForOpportunitiesByStatus,
} from "../../core/store/service-registrations/service-registrations.selectors";

@Component({
    selector: 'app-side-bar',
    templateUrl: './side-bar.component.html',
    styleUrls: ['./side-bar.component.scss'],
    standalone: false
})
export class SideBarComponent implements OnInit {
  public userName!: string | undefined;
  public userEmail!: string | undefined;
  private supervisorId: string | undefined;

  //isAdmin shows as false unless the logged in user is an admin
  public isAdmin$!: Observable<boolean | undefined>;

  public readonly PAST_TERM_REGISTRATIONS: AvailableToggle = AvailableToggle.PAST_REGISTRATION;
  public readonly OPPORTUNITY_REQUESTS: AvailableToggle = AvailableToggle.OPPORTUNITY_REQUESTS;

  public pastTermRegistrationRequestCount!: number;
  public actionItemsForPastOpportunitiesCount!: number;
  public actionItemsForFutureOpportunitiesCount!: number;
  public actionItemsForActiveOpportunitiesCount: Map<string, number> = new Map();

  @Output() public shouldCloseSideBar = new EventEmitter<boolean>();

  public ignoreImpersonate: Signal<boolean | undefined>;
  public isImpersonatingUser: Signal<boolean | undefined>;
  public isImpersonating: Signal<boolean | undefined>;

  constructor(
    private store: Store,
    private actions: Actions,
    private authService: AuthService,
    private supervisorService: SupervisorService,
    private router: Router,
    private userService: UserService,
    private featureToggleService: FeatureToggleService,
    private messageService: MessageService,
    private destroyRef: DestroyRef) {

    this.ignoreImpersonate = toSignal(this.featureToggleService.isToggleActive(AvailableToggle.LOSE_ADMIN_IMPERSONATOR_PERMISSIONS));
    this.isImpersonatingUser = toSignal(this.authService.isImpersonating());
    this.isImpersonating = computed(() => this.ignoreImpersonate() ? false : this.isImpersonatingUser() );
  }

  public opportunities$!: Observable<IOpportunity[]>;
  public organizations: ISidebarOrganization[] = [];
  public currentOppTerm: string = '';

  public isLoadingData$!: Observable<boolean>;
  public isContractSigned$!: Observable<boolean>;

  ngOnInit() {
    this.isAdmin$ = this.authService.isAdmin();
    this.isContractSigned$ = this.supervisorService.getSupervisorContractSigned();

    this.actions.pipe(
      ofType(updateOrganizationSuccessAction),
      startWith(null),
      switchMap(() =>
        this.supervisorService.getSidebarOrganizations().pipe(
          catchError(() => EMPTY)
        )
      ),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(organizations => {
      this.organizations = organizations;
    });

    this.supervisorService.getCurrentSupervisor().pipe(
      switchMap((supervisor: ISupervisor) => {
        this.userName = `${supervisor.firstName} ${supervisor.lastName}`;
        this.userEmail = supervisor.contactEmail;
        this.supervisorId = supervisor.id;
        this.getPastTermRegistrationsBy(supervisor);

        return combineLatest([
          this.supervisorService.getSupervisedOpportunitiesFromCurrentUser(),
          this.supervisorService.getMostCurrentSupervisedOpportunitiesFromCurrentUser()
        ]).pipe(
          tap(([opportunities, currentOpportunities]) => {
            this.getActionItemsForOpportunitiesByStatus(opportunities, currentOpportunities, OpportunityStatus.INACTIVE);
            this.getActionItemsForOpportunitiesByStatus(opportunities, currentOpportunities, OpportunityStatus.ACTIVE);
            this.currentOppTerm = currentOpportunities[0]?.term ?? 'NONE';

            this.opportunities$ = of(currentOpportunities);

          })
        );
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  public orgStatusDisplay(status: OrganizationStatus): string {
    switch (status) {
      case OrganizationStatus.INACTIVE:
        return 'Inactive';
      case OrganizationStatus.PENDING_ADMIN_SIGNATURE:
        return 'Pending';
      case OrganizationStatus.REJECTED:
        return 'Rejected';
      default:
        return '';
    }
  }

  public pastTermRegistrationButtonAriaLabel(): string {
    const count = this.pastTermRegistrationRequestCount;
    if (count > 0) {
      const itemLabel = count === 1 ? 'item' : 'items';
      return `Past Term Registrations, ${count} ${itemLabel} pending`;
    }
    return 'Past Term Registrations';
  }

  public oppStatusDisplay(status: OpportunityStatus): string {
    switch (status) {
      case OpportunityStatus.PENDING_ADMIN_SIGNATURE:
        return 'Pending';
      case OpportunityStatus.REJECTED:
        return 'Rejected';
      case OpportunityStatus.INACTIVE:
        return 'Inactive';
      default:
        return '';
    }
  }

  public navigateToOrgWithKeyboard(org: ISidebarOrganization) {
    this.router.navigate(org.route);
  }

  public navigateToOppWithKeyboard(oppId: string) {
    this.router.navigate(['/', 'supervisor', 'my-opportunities', oppId]);
  }

  public getOppRoute(opp: IOpportunity): string[] {
    if (opp.status === OpportunityStatus.REJECTED || opp.status === OpportunityStatus.PENDING_ADMIN_SIGNATURE) {
      return ['/', 'supervisor', 'opportunity', 'details', opp.id];
    } else {
      return ['/', 'supervisor', 'my-opportunities', opp.id];
    }
  }

  public logOut(): void {
    this.userService.signOut();
  }

  protected readonly OrganizationStatus = OrganizationStatus;

  public closeSideBar(): void {
    this.shouldCloseSideBar.emit(true);
  }

  public get isPastTermRegistrationFeatureEnabled(): Observable<boolean> {
    return this.featureToggleService.isToggleActive(this.PAST_TERM_REGISTRATIONS);
  }

  public get isOpportunityRequestFeatureEnabled(): Observable<boolean>{
    return this.featureToggleService.isToggleActive(this.OPPORTUNITY_REQUESTS);
  }

  public getActionItemCount(oppId: string): number {
    return this.actionItemsForActiveOpportunitiesCount?.get?.(oppId) ?? 0;
  }

  protected readonly OpportunityStatus = OpportunityStatus;

  private getPastTermRegistrationsBy(supervisor: ISupervisor): void {
    this.store.dispatch(loadPastTermRegistrations({ supervisorId: supervisor.id }));

    this.store.select(selectNewPastTermRegistrations)
      .pipe(
        distinctUntilChanged(),
        map((registrations: IServiceRegistration[]): number => registrations.length),
        catchError((): Observable<number> => {
          this.messageService.showErrorMessage('Error fetching past term registrations');
          return of(0);
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
        takeUntilDestroyed(this.destroyRef))
      .subscribe((count: number) => this.pastTermRegistrationRequestCount = count);
  }

  private getActionItemsForOpportunitiesByStatus(opportunities: IOpportunity[], currentOpportunities: IOpportunity[], status: OpportunityStatus): void {

    // If there is no supervisorId or no opportunities, we can skip the action items retrieval
    if (!this.supervisorId || opportunities.length === 0) {
      switch (status) {
        case OpportunityStatus.INACTIVE:  this.actionItemsForPastOpportunitiesCount = 0; break;
        case OpportunityStatus.ACTIVE:    this.actionItemsForActiveOpportunitiesCount.clear(); break;
        default:                          break;
      }
      return;
    }

    // Dispatch the action to find service registrations for the given opportunities and status
    this.store.dispatch(findServiceRegistrationsForOpportunitiesByStatus({
      supervisorId: this.supervisorId,
      opportunities: opportunities,
      status: status
    }));

    // Select the action items for the given opportunities and status
    this.store
      .pipe(
        select(selectActionItemsForOpportunitiesByStatus(this.supervisorId, opportunities, status)),
        map((serviceRegistrations: IServiceRegistration[] = []): number | Map<string, number> => {
          if (status === OpportunityStatus.ACTIVE) {
            const opportunityIdToCountMap: Map<string, number> = new Map<string, number>();
            for (const serviceRegistration of serviceRegistrations) {
              const opportunityId: string = serviceRegistration.oppId;
              const currentCount: number = opportunityIdToCountMap.get(opportunityId) ?? 0;
              opportunityIdToCountMap.set(opportunityId, currentCount + 1);
            }
            return opportunityIdToCountMap;
          } else return serviceRegistrations.length;
        }),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef))
      .subscribe((result: Map<string, number> | number): void => {
        // Set the action items count based on the status
        if (status === OpportunityStatus.ACTIVE) {
          this.actionItemsForActiveOpportunitiesCount = result as Map<string, number>;

          let futureActionCount = 0;
          this.actionItemsForActiveOpportunitiesCount.forEach((_, key: string) => {
            if (!currentOpportunities.some(opp => opp.id === key)) {
              futureActionCount++;
            }
          });
          this.actionItemsForFutureOpportunitiesCount = futureActionCount;
        } else {
          this.actionItemsForPastOpportunitiesCount = result as number;
        }
      });
  }

}
